import { useEffect, useState } from "react";

import { EHoverCard, EHoverDropdown, EHoverTarget } from "libs/ui";
import { IFlightOnlineBookingPaymentOrder } from "libs/data-access/types";

import { ListItem } from "./ListItem";
import { ECard } from "../Card/ECard";
import { RefundFeeSummary } from "./RefundFeeSummary";
import { getUserRole } from "utils/utility";

interface IProps {
  service: "FLIGHT" | "HOTEL";
  refundCharges: {
    bookingId: number;
    supplierCharge: number | null;
    cancellationCharge: number | null;
    cancellationChargeGst: number | null;
  }[];
  airPriceJourneyResponseList: IFlightOnlineBookingPaymentOrder | undefined;
};

const SectionTitle = ({ title }: { title: string }) => {
  return (
    <div className="text-sm font-semibold text-slate-900  mb-0.5">{title}</div>
  );
};

export default function FareSummary({ ...props }: IProps) {

  const [totalFare, setTotalFare] = useState<number>(0);
  const [commission, setCommission] = useState<number>(0);


  useEffect(() => {
    calculateTotalFare();
  }, []);


  // HANDLER FUNCTIONS
  const calculateTotalFare = () => {

    // commission fare: sum of all the commission charges set by the admin
    const commission =
      (props.airPriceJourneyResponseList?.commissionBaseFare || 0) +
      (props.airPriceJourneyResponseList?.commissionSurcharge || 0) +
      (props.airPriceJourneyResponseList?.commissionPlbBaseFare || 0) +
      (props.airPriceJourneyResponseList?.commissionPlbYQ || 0) +
      (props.airPriceJourneyResponseList?.commissionPlbYR || 0) +
      (props.airPriceJourneyResponseList?.commissionPlbBaseFareYQ || 0) +
      (props.airPriceJourneyResponseList?.commissionPlbBaseFareYR || 0);

    setCommission(Number(commission.toFixed(2)));

    // special service charges - extra baggage, meals, etc
    const ssrFare =
      (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarSsrPrice || 0) +
      (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSsrPrice || 0);

    // seat charges
    const seatFare =
      (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarSeatPrice || 0) +
      (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSeatPrice || 0);

    const totalFare =
      (props.airPriceJourneyResponseList?.galelioFlightOrderDetail?.flightBasePrice || 0) +
      (props.airPriceJourneyResponseList?.galelioFlightOrderDetail?.flightTaxPrice || 0) +
      (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoBasePrice || 0) +
      (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoTaxPrice || 0) +
      (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarBasePrice || 0) +
      (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarTaxPrice || 0) +
      (props.airPriceJourneyResponseList?.adminBaseCharge || 0) +
      (props.airPriceJourneyResponseList?.adminServiceCharge || 0) +
      (props.airPriceJourneyResponseList?.adminBookingCharge || 0) +
      (props.airPriceJourneyResponseList?.adminOtherCharge || 0) +
      (props.airPriceJourneyResponseList?.adminTotalGst || 0) +
      (props.airPriceJourneyResponseList?.affiliateBaseCharge || 0) +
      (props.airPriceJourneyResponseList?.affiliateServiceCharge || 0) +
      (props.airPriceJourneyResponseList?.affiliateBookingCharge || 0) +
      ssrFare +
      seatFare;
    // (props.airPriceJourneyResponseList?.affiliateOtherCharges || 0) +
    // (props.airPriceJourneyResponseList?.affiliateTotalGst || 0);

    setTotalFare(Number(totalFare.toFixed(2)));

  };


  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3">
        {/* base */}
        <div>
          <SectionTitle title="Air Fare" />
          {/* Base Fare: Includes the air base fare and markup base charge */}
          <ListItem
            title="Base Charge"
            value={
              (props.airPriceJourneyResponseList?.galelioFlightOrderDetail?.flightBasePrice || 0) +
              (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoBasePrice || 0) +
              (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarBasePrice || 0)
            }
          />

          {/* Tax & Charges: Includes air taxes and markup service charge */}
          <ListItem
            title="Tax & Charges"
            value={
                (props.airPriceJourneyResponseList?.galelioFlightOrderDetail?.flightTaxPrice || 0) +
                (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoTaxPrice || 0) +
                (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarTaxPrice || 0) + 
                (
                  getUserRole().includes("AFFILIATE")
                    ? (props.airPriceJourneyResponseList?.adminBaseCharge || 0) +
                      (props.airPriceJourneyResponseList?.adminServiceCharge || 0) +
                      (props.airPriceJourneyResponseList?.adminBookingCharge || 0)
                    : 0
                )
              }
          />

          {/* SSR */}
          {
            (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSsrPrice
              ||
              props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarSsrPrice)
              ?
              <ListItem
                title="Special Service"
                value={
                  (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSsrPrice || 0) +
                  (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarSsrPrice || 0) +
                  (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSeatPrice || 0)
                }
              />
              :
              <></>
          }

          {/* Seat Price */}
          {/* {
            props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSeatPrice
              ?
              <ListItem
                title="Seat Price"
                value={
                  (props.airPriceJourneyResponseList?.naviatorOrderDetail?.indigoSeatPrice || 0)
                  // +
                  // (props.airPriceJourneyResponseList?.akbarOrderDetail?.akbarSeatPrice || 0)
                }
              />
              :
              <></>
          } */}
        </div>

        {/* Admin Markup: total markup */}
        {getUserRole().includes("ADMIN") ?
        <div>
          <SectionTitle title="Admin - My Markup" />
          <EHoverCard withArrow width={350} position="bottom-start">
            
            <EHoverTarget>
              <div className="cursor-pointer">
                <ListItem
                  title="Total Markup"
                  value={
                    (props.airPriceJourneyResponseList?.adminBaseCharge || 0) +
                    (props.airPriceJourneyResponseList?.adminServiceCharge || 0) +
                    (props.airPriceJourneyResponseList?.adminBookingCharge || 0)
                  }
                />
              </div>
            </EHoverTarget>
            
            <EHoverDropdown>
              <div className="flex flex-col gap-2">
                <div className="font-semibold text-sm text-slate-900 mb-2">Admin Markup Breakdown</div>
                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Base Charge"
                  value={props.airPriceJourneyResponseList?.adminBaseCharge || 0}
                />

                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Service Charge"
                  value={props.airPriceJourneyResponseList?.adminServiceCharge || 0}
                />

                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Booking Charge"
                  value={props.airPriceJourneyResponseList?.adminBookingCharge || 0}
                />
              </div>
            </EHoverDropdown>

          </EHoverCard>
        </div>
        :<>
        </>
        }

        {/* Affiliate Markup: total markup */}
        <div>
          <SectionTitle title="Affiliate - My Markup" />
          <EHoverCard withArrow width={350} position="bottom-start">
            <EHoverTarget>
              <div className="cursor-pointer">
                <ListItem
                  title="Total Markup"
                  value={
                    (props.airPriceJourneyResponseList?.affiliateBaseCharge || 0) +
                    (props.airPriceJourneyResponseList?.affiliateServiceCharge || 0) +
                    (props.airPriceJourneyResponseList?.affiliateBookingCharge || 0)
                  }
                />
              </div>
            </EHoverTarget>
            <EHoverDropdown>
              <div className="flex flex-col gap-2">
                <div className="font-semibold text-sm text-slate-900 mb-2">Affiliate Markup Breakdown</div>
                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Base Charge"
                  value={props.airPriceJourneyResponseList?.affiliateBaseCharge || 0}
                />

                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Service Charge"
                  value={props.airPriceJourneyResponseList?.affiliateServiceCharge || 0}
                />

                {/* Other Charges: Includes other charge and markup booking charge */}
                <ListItem
                  title="Booking Charge"
                  value={props.airPriceJourneyResponseList?.affiliateBookingCharge || 0}
                />
              </div>
            </EHoverDropdown>
          </EHoverCard>
        </div>

        {/* other charges */}
        <div>
          <SectionTitle title="Other Charges" />

          {/* Other Charges: Includes other charge and markup booking charge */}
          <ListItem
            title="Other Charges"
            value={props.airPriceJourneyResponseList?.adminOtherCharge || 0}
          />

          {/* GST: Includes total gst */}
          <ListItem
            title="GST (All Inclusive)"
            value={props.airPriceJourneyResponseList?.adminTotalGst || 0}
          />
        </div>

        {/* Commission: commission charge */}
        <div>
          <SectionTitle title="Commission" />
          <EHoverCard withArrow width={350} position="bottom-start">
            <EHoverTarget>
              <div className="cursor-pointer">
                <ListItem
                  title="Commission (-)"
                  value={commission}
                />
              </div>
            </EHoverTarget>
            <EHoverDropdown>
              <div className="flex flex-col gap-2">
                <div className="font-semibold text-sm text-slate-900 mb-2">Commission Breakdown</div>
                {
                  props.airPriceJourneyResponseList?.commissionBaseFare
                    ?
                    <ListItem
                      title="Base fare"
                      value={props.airPriceJourneyResponseList?.commissionBaseFare || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionSurcharge
                    ?
                    <ListItem
                      title="YQ"
                      value={props.airPriceJourneyResponseList?.commissionSurcharge || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionPlbBaseFare
                    ?
                    <ListItem
                      title="Base fare (PLB)"
                      value={props.airPriceJourneyResponseList?.commissionPlbBaseFare || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionPlbYQ
                    ?
                    <ListItem
                      title="YQ (PLB)"
                      value={props.airPriceJourneyResponseList?.commissionPlbYQ || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionPlbYR
                    ?
                    <ListItem
                      title="YR (PLB)"
                      value={props.airPriceJourneyResponseList?.commissionPlbYR || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionPlbBaseFareYQ
                    ?
                    <ListItem
                      title="Base + YQ (PLB)"
                      value={props.airPriceJourneyResponseList?.commissionPlbBaseFareYQ || 0}
                    />
                    :
                    <></>
                }

                {
                  props.airPriceJourneyResponseList?.commissionPlbBaseFareYR
                    ?
                    <ListItem
                      title="Base + YR (PLB)"
                      value={props.airPriceJourneyResponseList?.commissionPlbBaseFareYR || 0}
                    />
                    :
                    <></>
                }
              </div>
            </EHoverDropdown>
          </EHoverCard>

          {/* TDS: Includes total tds */}
          <ListItem
            title="TDS (+)"
            value={props.airPriceJourneyResponseList?.commissionTds || 0}
          />
        </div>

        {/* total */}
        <div className=" bg-gray-100 py-1 px-2 rounded-md font-semibold text-lg">
          <ListItem
            title="Total"
            showPrefix={true}
            value={Number(
              ((totalFare + (props.airPriceJourneyResponseList?.commissionTds || 0)) - commission).toFixed(2)
            )}
          />
        </div>
      </div>

      {/* Refund Summary */}
      {
        props.refundCharges.some(each => each?.supplierCharge && each?.supplierCharge > 0) &&
        <ECard
          title="Refund Summary"
        >
          <RefundFeeSummary
            service="FLIGHT"
            totalFare={Number(
              ((totalFare + (props.airPriceJourneyResponseList?.commissionTds || 0)) - commission).toFixed(2)
            )}
            refundCharges={props.refundCharges}
          />
        </ECard>
      }
    </div>
  );
}